import React, { useState, FC, useCallback } from 'react';
import './Canvas.scss';
import { scaleBackImages, StitchingManipulationRules } from '../../../shared/Helpers';
import ConfirmationDialog from './ConfirmationDialog';
import Toolbar from './Toolbar/Toolbar';

import Canvas from './Canvas';

export type CanvasWithToolbarProps = {
  images: any[];
  subsampleFactor: number;
  id: string;
  isStitched: boolean;
  isRejected: boolean;
  isToolbarBusy: boolean;
  canStitch: boolean;
  onSaveImage: (images: any[], finish: boolean) => void;
  onRejectSequence: () => void;
  onImagesLoaded: () => void;
};

type ScaleBackImagesResult = ReturnType<typeof scaleBackImages>;

const CanvasWithToolbar: FC<CanvasWithToolbarProps> = ({
  images,
  subsampleFactor,
  id,
  isStitched,
  isRejected,
  isToolbarBusy,
  canStitch,
  onSaveImage,
  onImagesLoaded,
  onRejectSequence
}) => {
  const [resetCanvas, setResetCanvas] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [selectAllImages, setSelectAllImages] = useState(false);
  const [doUndo, setDoUndo] = useState(false);
  const [doRedo, setDoRedo] = useState(false);

  const [showCompleteStitchingModal, setShowCompleteStitchingModal] = useState(false);
  const [showRejectSequenceModal, setShowRejectSequenceModal] = useState(false);
  const [showHideImageModal, setShowHideImageModal] = useState(false);
  const [hideImages, setHideImages] = useState(false);
  const [anyActiveObject, setAnyActiveObject] = useState(false);
  const [scaledImages, setScaledImages] = useState<ScaleBackImagesResult>([]);

  function selectAllObjects() {
    setSelectAllImages(true);
  }

  function onRotateStart() {
    setIsRotating(true);
  }

  function onRotateEnd() {
    setIsRotating(false);
  }

  function resetCanvasHandler() {
    setResetCanvas(!resetCanvas);
  }

  function rejectSequenceClicked() {
    setShowRejectSequenceModal(true);
  }

  function onSaveStiching() {
    saveStiching(false);
  }

  function onFinishStitching() {
    setShowCompleteStitchingModal(true);
  }

  function showHideImageModalHandler() {
    setShowHideImageModal(true);
  }

  const objectSelectionHandler = useCallback((isAnyActive: boolean, areAllActive: boolean) => {
    setAnyActiveObject(isAnyActive);
    if (!areAllActive) {
      setSelectAllImages(false);
    }
  }, []);

  const imageChangeHandler = useCallback((scaledImages: ScaleBackImagesResult) => {
    setScaledImages(scaledImages);
  }, []);

  function saveStiching(isFinished: boolean) {
    const alreadyHiddenImages = images.filter(x => x.isHidden);
    onSaveImage([...scaledImages, ...alreadyHiddenImages], isFinished);
    setShowCompleteStitchingModal(false);
  }

  const imagesHiddenHandler = useCallback(() => {
    setHideImages(false);
    setShowHideImageModal(false);
  }, []);

  function onHideImage() {
    setHideImages(true);
  }

  return (
    <div id='container' style={{ position: 'relative' }}>
      {canStitch && (
        <Toolbar
          selectAllClicked={selectAllObjects}
          rotateStart={onRotateStart}
          rotateEnd={onRotateEnd}
          saveClicked={onSaveStiching}
          resetClicked={resetCanvasHandler}
          completeClicked={onFinishStitching}
          isStitched={isStitched}
          isRejected={isRejected}
          rejectSequenceClicked={rejectSequenceClicked}
          isToolbarBusy={isToolbarBusy}
          anyActive={anyActiveObject}
          onUndoClick={shouldDo => setDoUndo(shouldDo)}
          onRedoClick={shouldDo => setDoRedo(shouldDo)}
        />
      )}
      <Canvas
        id={id}
        rawImages={images}
        subsampleFactor={subsampleFactor}
        isStitched={isStitched}
        resetCanvas={resetCanvas}
        isRotating={isRotating}
        selectAllImages={selectAllImages}
        hideImages={hideImages}
        onImagesHidden={imagesHiddenHandler}
        onImagesLoaded={onImagesLoaded}
        onShowHideImageModal={showHideImageModalHandler}
        onImageChange={imageChangeHandler}
        onObjectSelected={objectSelectionHandler}
        doRedo={doRedo}
        doUndo={doUndo}
      />
      <ConfirmationDialog
        show={showCompleteStitchingModal}
        bodyMessage='Are you sure that you want to complete stitching? Once stitching is
        completed you cannot stitch again.'
        onConfirm={() => saveStiching(true)}
        onHide={() => setShowCompleteStitchingModal(false)}
      />
      <ConfirmationDialog
        show={showRejectSequenceModal}
        bodyMessage='Are you sure you want to reject sequence?'
        onConfirm={onRejectSequence}
        onHide={() => setShowRejectSequenceModal(false)}
      />
      <ConfirmationDialog
        show={showHideImageModal}
        bodyMessage='Are you sure you want to hide this image?'
        onConfirm={onHideImage}
        onHide={() => setShowHideImageModal(false)}
      />
    </div>
  );
};

export default CanvasWithToolbar;
